// AIAgentBot.js - With Status Icons for URLs and Files
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import appStyle from "./AIAgentBot.module.css";
import DataIdsButton from "./DataIdsButton";
import AIMessageCredits from "./AIMessageCredits";
import IngestionStorage from "./IngestionStorage";
import ProductInfo from "../SideData/Overview/ProductInfo/ProductInfo";
import modalContentSty from "../SideData/Overview/LeadGenBotSettings/ModalContent.module.css";
import Modal from "../Modal";
import { useTenantPlan } from "../../hooks/useTenantPlan";
import { FaInfoCircle, FaSpinner, FaCheckCircle, FaTimesCircle } from "react-icons/fa";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const normalizeUrlsStructure = (urls) => {
  if (!urls) return { ingested: [], failed: [] };

  if (urls.ingested !== undefined || urls.failed !== undefined) {
    return {
      ingested: urls.ingested || [],
      failed: urls.failed || [],
      storage_used_kb: urls.storage_used_kb,
      storage_limit_kb: urls.storage_limit_kb,
      count: urls.count,
      failed_count: urls.failed_count,
      stopped_reason: urls.stopped_reason,
      overall_status: urls.overall_status,
    };
  }

  if (Array.isArray(urls)) {
    return {
      ingested: urls,
      failed: [],
    };
  }

  return { ingested: [], failed: [] };
};

const normalizeFilesStructure = (files) => {
  if (!files) return { ingested: [], failed: [] };

  if (files.ingested !== undefined || files.failed !== undefined) {
    return {
      ingested: files.ingested || [],
      failed: files.failed || [],
      storage_used_kb: files.storage_used_kb,
      storage_limit_kb: files.storage_limit_kb,
      count: files.count,
      failed_count: files.failed_count,
      stopped_reason: files.stopped_reason,
      overall_status: files.overall_status,
    };
  }

  if (Array.isArray(files)) {
    return {
      ingested: files,
      failed: [],
    };
  }

  return { ingested: [], failed: [] };
};

const getToken = () => localStorage.getItem("token");

// Tooltip component
const InfoTooltip = ({ text }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <span
      className={appStyle.infoTooltipWrapper}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <FaInfoCircle className={appStyle.infoIcon} />
      {showTooltip && <span className={appStyle.infoTooltip}>{text}</span>}
    </span>
  );
};

// Status Icon component
const StatusIcon = ({ status, isTraining }) => {
  if (isTraining) {
    return <FaSpinner className={`${appStyle.statusIcon} ${appStyle.statusLoading}`} />;
  }
  if (status === 'success') {
    return <FaCheckCircle className={`${appStyle.statusIcon} ${appStyle.statusSuccess}`} />;
  }
  if (status === 'failed') {
    return <FaTimesCircle className={`${appStyle.statusIcon} ${appStyle.statusFailed}`} />;
  }
  return null;
};

export default function AIAgentBot({
  botContent,
  setBotContent,
  tenantPlan,
  onTrainingRequest,
  fromWhere,
}) {
  console.log("tenantPlandhhhhhhhhhh", botContent);
  const {
    data: tenantPlanData,
    loading: planLoading,
    error: planError,
    refetch,
  } = useTenantPlan();

  console.log("tenantPlan from hook:", tenantPlanData);

  // Extract kbId, tenantId, appName, region from botContent
  const kbId = botContent?.kb_id;
  const tenantId = botContent?.tenant;
  const appName = botContent?.app_name;
  const region = botContent?.region;

  const aiMessageLimit = tenantPlanData?.aimm_limit || 0;
  const aiMessagesRemaining = tenantPlanData?.global_ai_msgs_remaining || 0;
  const totalAiTopups = tenantPlanData?.total_topups_ai_messages || 0;
  const isLoadingAiLimit = planLoading;

  const ingestionLimit = tenantPlanData?.max_ingest_limit || 0;
  const ingestionRemaining = tenantPlanData?.global_ingest_size_remaining || 0;
  const totalIngestionTopups = tenantPlanData?.total_topups_ingestion || 0;
  const isLoadingIngestionLimit = planLoading;

  const [showAiMessageCredits, setShowAiMessageCredits] = useState(false);
  const [showIngestionStorage, setShowIngestionStorage] = useState(false);
  const [showDataIds, setShowDataIds] = useState(false);
  const [showFailedUrls, setShowFailedUrls] = useState(false);
  const [showFailedFiles, setShowFailedFiles] = useState(false);
  const [fileUploadErrors, setFileUploadErrors] = useState({});

  // Product Info Dropdown State
  const [showProductInfo, setShowProductInfo] = useState(false);
  const [productInfoLoading, setProductInfoLoading] = useState(true);
  const [productInfoHasData, setProductInfoHasData] = useState(false);

  // Product Info Modal State
  const [showProductInfoModal, setShowProductInfoModal] = useState(false);
  const [pendingRetrainAction, setPendingRetrainAction] = useState(null);

  // Ref for inline ProductInfo component
  const productInfoRef = useRef(null);

  // Use ref to track if we've initialized
  const isInitializedRef = useRef(false);

  console.log("botContentData", JSON.stringify(botContent));
  console.log("here is botContent isTraining flag", botContent.isTraining);

  const ingestionMethods = botContent?.ingestion_methods || {
    files: { ingested: [], failed: [] },
    urls: { ingested: [], failed: [] },
    sitemap: null,
  };

  console.log(
    "botContent?.ingestion_methods",
    JSON.stringify(botContent?.ingestion_methods),
  );

  const files = normalizeFilesStructure(
    ingestionMethods.files || { ingested: [], failed: [] },
  );
  const urls = normalizeUrlsStructure(
    ingestionMethods.urls || { ingested: [], failed: [] },
  );
  const { sitemap } = ingestionMethods;

  const hasFiles = files.ingested.length > 0 || files.failed.length > 0;
  const hasUrls = urls.ingested.length > 0 || urls.failed.length > 0;
  const hasSitemap = !!sitemap;

  // Handle product info data fetched callback
  const handleProductInfoFetched = useCallback((hasData) => {
    console.log("📦 Product info fetched, hasData:", hasData);
    setProductInfoLoading(false);
    setProductInfoHasData(hasData);
    // If no data exists, keep dropdown open; if data exists, close it
    setShowProductInfo(!hasData);
  }, []);

  // Check if product info exists (for retrain check)
  const checkProductInfoExists = useCallback(async () => {
    if (!kbId) return false;

    try {
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_HF_INGEST_URL}/ai_ingest_sys?task=product-metadata&kb_id=${kbId}`,
        {
          method: "GET",
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const result = await response.json();

        // Handle the response properly - check if we need to parse the body
        let data = result;

        // If the response has a body that's a string, parse it
        if (result.body && typeof result.body === "string") {
          try {
            data = JSON.parse(result.body);
          } catch (parseError) {
            console.error(
              "Error parsing product metadata response:",
              parseError,
            );
            return false;
          }
        }

        // Check if status is success AND data exists AND data has content
        if (data.status === "success" && data.data) {
          // Check if there's actual data (not empty)
          const hasProductName =
            data.data.product_name && data.data.product_name.trim() !== "";
          const hasProductSummary =
            data.data.product_summary &&
            data.data.product_summary.trim() !== "";
          return hasProductName || hasProductSummary;
        }
        return false;
      }
      return false;
    } catch (error) {
      console.error("Error checking product info:", error);
      return false;
    }
  }, [kbId]);

  // Local state
  const [localSitemap, setLocalSitemap] = useState("");
  const [localUrls, setLocalUrls] = useState([]);
  const [localFiles, setLocalFiles] = useState([]);
  const [urlErrors, setUrlErrors] = useState({});
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [trainingMessage, setTrainingMessage] = useState("");
  const [trainingResults, setTrainingResults] = useState(null);
  const [allowNested, setAllowNested] = useState(false);
  const [globalExploreClickables, setGlobalExploreClickables] = useState(false);
  const [filesEnabled, setFilesEnabled] = useState(false);
  const [urlsEnabled, setUrlsEnabled] = useState(false);
  const [sitemapEnabled, setSitemapEnabled] = useState(false);

  // isTraining comes from botContent - this is the actual training state from backend
  const isTraining = botContent.isTraining === 1;

  // Clear training message when training actually starts
  useEffect(() => {
    if (isTraining) {
      // Training has started, clear the "ready" message
      setTrainingStatus(null);
      setTrainingMessage('');
      setTrainingResults(null);
    }
  }, [isTraining]);

  // Initialize local state from props - only once
  useEffect(() => {
    if (!isInitializedRef.current && botContent) {
      console.log("🔄 Initializing AIAgentBot state from props");

      setLocalSitemap(sitemap || "");
      setLocalUrls(
        urls.ingested.map((url) => ({
          url: url.url || "",
          force_reingest: false,
          force_delete: false,
          explore_clickables: url.explore_clickables || false,
          isNew: false,
          status: 'success', // Mark as success since it's already ingested
        })),
      );
      setLocalFiles(
        files.ingested.map((file) => ({
          fileName: file.fileName || "",
          data_ids: file.data_ids || [],
          fileObject: null,
          force_reingest: false,
          force_delete: false,
          isNew: false,
          status: 'success', // Mark as success since it's already ingested
        })),
      );
      setAllowNested(botContent.allow_nested || false);
      setGlobalExploreClickables(botContent.explore_clickables || false);

      setFilesEnabled(hasFiles);
      setUrlsEnabled(hasUrls || (!hasFiles && !hasSitemap));

      setSitemapEnabled(hasSitemap);

      isInitializedRef.current = true;
    }
  }, [
    botContent,
    sitemap,
    urls.ingested,
    files.ingested,
    hasFiles,
    hasUrls,
    hasSitemap,
  ]);

  const isValidUrl = (url) => {
    if (!url.trim()) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === "http:" || urlObj.protocol === "https:";
    } catch {
      return false;
    }
  };

  const validateUrls = (urlsArray) => {
    const errors = {};
    urlsArray.forEach((urlObj, index) => {
      if (urlObj.url.trim() && !isValidUrl(urlObj.url)) {
        errors[index] = "Please enter a valid URL (e.g., https://example.com)";
      }
    });
    setUrlErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const hasInvalidUrls = Object.keys(urlErrors).length > 0;

  // Check if there are changes
  const hasChanges = useMemo(() => {
    const initialFilesEnabled = hasFiles;
    const initialUrlsEnabled = hasUrls || (!hasFiles && !hasSitemap);
    const initialSitemapEnabled = hasSitemap;

    if (filesEnabled !== initialFilesEnabled) return true;
    if (urlsEnabled !== initialUrlsEnabled) return true;
    if (sitemapEnabled !== initialSitemapEnabled) return true;

    if (allowNested !== (botContent.allow_nested || false)) return true;

    if (globalExploreClickables !== (botContent.explore_clickables || false))
      return true;

    if (localSitemap !== (sitemap || "")) return true;

    const initialUrls = urls.ingested.map((u) => u.url || "");
    const currentUrls = localUrls.map((u) => u.url);
    if (JSON.stringify(currentUrls) !== JSON.stringify(initialUrls))
      return true;

    const hasUrlOptions = localUrls.some((url, i) => {
      if (i < urls.ingested.length) {
        return url.force_reingest || url.force_delete || url.explore_clickables;
      }
      return true;
    });
    if (hasUrlOptions) return true;

    const initialFiles = files.ingested.map((f) => f.fileName || "");
    const currentFiles = localFiles.map((f) => f.fileName);
    if (JSON.stringify(currentFiles) !== JSON.stringify(initialFiles))
      return true;

    const hasFileOptions = localFiles.some((file, i) => {
      if (i < files.ingested.length) {
        return file.force_reingest || file.force_delete;
      }
      return true;
    });
    if (hasFileOptions) return true;

    return false;
  }, [
    filesEnabled,
    urlsEnabled,
    sitemapEnabled,
    allowNested,
    globalExploreClickables,
    localSitemap,
    localUrls,
    localFiles,
    botContent,
    sitemap,
    urls.ingested,
    files.ingested,
    hasFiles,
    hasUrls,
    hasSitemap,
  ]);

  // Check if retrain is possible
  const canRetrain = useMemo(() => {
    // Don't allow retrain if currently training
    if (isTraining) return false;

    const hasFilesToTrain =
      filesEnabled &&
      localFiles.length > 0 &&
      Object.keys(fileUploadErrors).length === 0;
    const hasUrlsToTrain =
      urlsEnabled &&
      localUrls.some((urlObj) => urlObj.url.trim() !== "") &&
      !hasInvalidUrls;
    const hasSitemapToTrain = sitemapEnabled && localSitemap.trim() !== "";

    return hasFilesToTrain || hasUrlsToTrain || hasSitemapToTrain;
  }, [
    filesEnabled,
    localFiles,
    fileUploadErrors,
    urlsEnabled,
    localUrls,
    hasInvalidUrls,
    sitemapEnabled,
    localSitemap,
    isTraining,
  ]);

  const toggleAIAgentBot = () => {
    if (isTraining) return;

    setBotContent((prev) => ({
      ...prev,
      ai_agent_bot_enabled: !prev.ai_agent_bot_enabled,
    }));
  };

  const handleCheckboxChange = (method) => {
    switch (method) {
      case "files":
        setFilesEnabled(!filesEnabled);
        if (filesEnabled) {
          setLocalFiles([]);
        }
        break;
      case "urls":
        setUrlsEnabled(!urlsEnabled);
        if (urlsEnabled) {
          setLocalUrls([]);
          setUrlErrors({});
        }
        break;
      case "sitemap":
        setSitemapEnabled(!sitemapEnabled);
        if (!sitemapEnabled) {
          setLocalSitemap("");
        }
        break;
      default:
        break;
    }
  };

  // Handle global explore clickables change - updates all URLs
  const handleGlobalExploreClickablesChange = (checked) => {
    setGlobalExploreClickables(checked);
    
    // When checked, enable explore_clickables for all URLs
    // When unchecked, don't change individual settings (keep them as they are)
    if (checked) {
      setLocalUrls(prevUrls => 
        prevUrls.map(url => ({
          ...url,
          explore_clickables: true
        }))
      );
    }
  };

  const handleUrlChange = (index, value) => {
    const newUrls = [...localUrls];
    newUrls[index] = {
      ...newUrls[index],
      url: value,
    };
    setLocalUrls(newUrls);
    validateUrls(newUrls);
  };

  const handleForceReingestChange = (index, checked) => {
    const newUrls = [...localUrls];
    newUrls[index] = {
      ...newUrls[index],
      force_reingest: checked,
      force_delete: checked ? false : newUrls[index].force_delete,
    };
    setLocalUrls(newUrls);
  };

  const handleForceDeleteChange = (index, checked) => {
    const newUrls = [...localUrls];
    newUrls[index] = {
      ...newUrls[index],
      force_delete: checked,
      force_reingest: checked ? false : newUrls[index].force_reingest,
    };
    setLocalUrls(newUrls);
  };

  const handleExploreClickablesChange = (index, checked) => {
    const newUrls = [...localUrls];
    newUrls[index] = {
      ...newUrls[index],
      explore_clickables: checked,
    };
    setLocalUrls(newUrls);
  };

  const addUrlInput = () => {
    const newUrls = [
      ...localUrls,
      {
        url: "",
        force_reingest: false,
        force_delete: false,
        // If global is enabled, new URLs get explore_clickables: true
        explore_clickables: globalExploreClickables,
        isNew: true,
        status: null, // No status for new URLs
      },
    ];
    setLocalUrls(newUrls);
  };

  const removeUrlInput = (index) => {
    const newUrls = localUrls.filter((_, i) => i !== index);
    setLocalUrls(newUrls);

    const newErrors = { ...urlErrors };
    delete newErrors[index];

    const shiftedErrors = {};
    Object.keys(newErrors).forEach((errorIndex) => {
      const numIndex = parseInt(errorIndex);
      if (numIndex > index) {
        shiftedErrors[numIndex - 1] = newErrors[errorIndex];
      } else {
        shiftedErrors[numIndex] = newErrors[errorIndex];
      }
    });

    setUrlErrors(shiftedErrors);
    validateUrls(newUrls);
  };

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);

    if (selectedFiles.length > 0) {
      const newFiles = selectedFiles.map((file) => ({
        fileName: file.name,
        data_ids: [],
        fileObject: file,
        force_reingest: false,
        force_delete: false,
        isNew: true,
        status: null, // No status for new files
      }));

      setLocalFiles((prev) => [...prev, ...newFiles]);
      event.target.value = "";
    }
  };

  const handleReingestFileUpload = (index, event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const newFiles = [...localFiles];
      newFiles[index] = {
        ...newFiles[index],
        fileObject: selectedFile,
        status: null, // Reset status when re-uploading
      };
      setLocalFiles(newFiles);
      setFileUploadErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      event.target.value = "";
    }
  };

  const handleFileForceReingestChange = (index, checked) => {
    const newFiles = [...localFiles];
    newFiles[index] = {
      ...newFiles[index],
      force_reingest: checked,
      force_delete: checked ? false : newFiles[index].force_delete,
      fileObject: checked ? newFiles[index].fileObject : null,
      status: checked ? null : newFiles[index].status, // Reset status when enabling force reingest
    };
    setLocalFiles(newFiles);

    if (!checked) {
      setFileUploadErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const handleFileForceDeleteChange = (index, checked) => {
    const newFiles = [...localFiles];
    newFiles[index] = {
      ...newFiles[index],
      force_delete: checked,
      force_reingest: checked ? false : newFiles[index].force_reingest,
      status: checked ? null : newFiles[index].status, // Reset status when enabling force delete
    };
    setLocalFiles(newFiles);
  };

  const removeFile = (index) => {
    setLocalFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setLocalSitemap(sitemap || "");
    setLocalUrls(
      urls.ingested.map((url) => ({
        url: url.url || "",
        force_reingest: false,
        force_delete: false,
        explore_clickables: url.explore_clickables || false,
        isNew: false,
        status: 'success',
      })),
    );
    setLocalFiles(
      files.ingested.map((file) => ({
        fileName: file.fileName || "",
        data_ids: file.data_ids || [],
        fileObject: null,
        force_reingest: false,
        force_delete: false,
        isNew: false,
        status: 'success',
      })),
    );
    setAllowNested(botContent.allow_nested || false);
    setGlobalExploreClickables(botContent.explore_clickables || false);
    setFilesEnabled(hasFiles);
    setUrlsEnabled(hasUrls || (!hasFiles && !hasSitemap));
    setSitemapEnabled(hasSitemap);
    validateUrls(
      urls.ingested.map((url) => ({
        url: url.url || "",
        force_reingest: false,
        force_delete: false,
        explore_clickables: false,
        isNew: false,
      })),
    );
    setFileUploadErrors({});
  };

  const prepareTrainingData = () => {
    const meData = JSON.parse(localStorage.getItem("meData"));

    return {
      kb_id:
        botContent.kb_id ||
        `${meData.tenant_info.tenant}__${botContent.app_name}`,
      tenant: meData.tenant_info.tenant,
      app_name: botContent.app_name,
      region: botContent.region,
      urlsEnabled,
      sitemapEnabled,
      filesEnabled,
      allowNested,
      globalExploreClickables,
      localUrls: localUrls
        .filter((urlObj) => urlObj.url.trim() !== "")
        .map((urlObj) => ({
          url: urlObj.url.trim(),
          force_reingest: urlObj.force_reingest,
          force_delete: urlObj.force_delete,
          // Use the individual explore_clickables value, but if global is enabled and individual is true, it's true
          explore_clickables: globalExploreClickables ? true : urlObj.explore_clickables,
          isNew: urlObj.isNew,
        })),
      localSitemap: localSitemap.trim(),
      localFiles: localFiles
        .filter((file) => file.fileName.trim() !== "")
        .map((file) => ({
          fileName: file.fileName,
          force_reingest: file.force_reingest,
          force_delete: file.force_delete,
          isNew: file.isNew,
          fileObject: file.fileObject,
        })),
    };
  };

  const executeRetrain = async () => {
    console.log("🚀 Executing retrain...");

    const newFileUploadErrors = {};
    localFiles.forEach((file, index) => {
      if (!file.isNew && file.force_reingest && !file.fileObject) {
        newFileUploadErrors[index] = "Please upload a new file for reingestion";
      }
    });

    if (Object.keys(newFileUploadErrors).length > 0) {
      setFileUploadErrors(newFileUploadErrors);
      setTrainingStatus("error");
      setTrainingMessage(
        "Please upload new files for all files with Force Reingest enabled.",
      );
      return;
    }

    if (!canRetrain) {
      console.log("❌ Cannot retrain - no valid data");
      return;
    }

    setTrainingStatus("ready");
    setTrainingMessage(
      'Training data prepared. Click "Update AI Chat" to start training.',
    );
    setTrainingResults(null);

    try {
      console.log("API_CALL_FLOW: Preparing training data");
      const trainingConfig = prepareTrainingData();

      console.log("API_CALL_FLOW: Training config prepared:", trainingConfig);

      if (onTrainingRequest) {
        console.log(
          "API_CALL_FLOW: Calling onTrainingRequest with config",
          trainingConfig,
        );
        onTrainingRequest(trainingConfig);
      } else {
        console.log("API_CALL_FLOW: onTrainingRequest is not available");
      }
    } catch (error) {
      console.error("API_CALL_FLOW: Error preparing training data:", error);
      setTrainingStatus("error");
      setTrainingMessage(`Failed to prepare training data: ${error.message}`);
    }
  };

  const handleRetrainAll = async () => {
    console.log("🚀 Retrain button clicked");

    // Check if product info exists
    const hasProductInfo = await checkProductInfoExists();

    if (!hasProductInfo) {
      // Show modal to collect product info
      console.log("📦 No product info found, showing modal...");
      setPendingRetrainAction(() => executeRetrain);
      setShowProductInfoModal(true);
      return;
    }

    // Product info exists, proceed with retrain
    await executeRetrain();
  };

  const handleProductInfoSaveSuccess = async () => {
    console.log(
      "✅ Product info saved successfully, refreshing inline component...",
    );

    // Refresh the inline ProductInfo component
    if (productInfoRef.current && productInfoRef.current.refresh) {
      await productInfoRef.current.refresh();
    }

    // Update dropdown state - close it since data now exists
    setShowProductInfo(false);
    setProductInfoHasData(true);

    setShowProductInfoModal(false);

    // Execute the pending retrain action
    if (pendingRetrainAction) {
      setTimeout(() => {
        pendingRetrainAction();
        setPendingRetrainAction(null);
      }, 100);
    }
  };

  const handleSkipProductInfo = () => {
    // Close modal and proceed with retrain using existing data
    setShowProductInfoModal(false);
    if (pendingRetrainAction) {
      setTimeout(() => {
        pendingRetrainAction();
        setPendingRetrainAction(null);
      }, 100);
    }
  };

  const handleModalClose = () => {
    setShowProductInfoModal(false);
    setPendingRetrainAction(null);
  };

  const getAllFailedUrls = () => {
    const failedUrls = [...(urls.failed || [])];

    if (trainingResults?.results?.urls?.failed_urls) {
      trainingResults.results.urls.failed_urls.forEach((failedUrl) => {
        if (!failedUrls.some((existing) => existing.url === failedUrl.url)) {
          failedUrls.push(failedUrl);
        }
      });
    }

    return failedUrls;
  };

  const getAllFailedFiles = () => {
    const failedFiles = [...(files.failed || [])];

    if (trainingResults?.results?.files?.failed) {
      trainingResults.results.files.failed.forEach((failedFile) => {
        if (
          !failedFiles.some(
            (existing) => existing.fileName === failedFile.fileName,
          )
        ) {
          failedFiles.push(failedFile);
        }
      });
    }

    return failedFiles;
  };

  // Determine button text based on training state
  const getButtonText = () => {
    if (isTraining) {
      return "Training...";
    }
    // If trainingStatus is 'ready' or there's a training message, show Ready to Train
    if (trainingStatus === 'ready' || (trainingMessage && trainingMessage.includes('prepared'))) {
      return "Ready to Train";
    }
    return "Retrain";
  };

  return (
    <div className={appStyle.aiAgentSection}>
      <div className={appStyle.toggleSection}>
        <label className={appStyle.toggleLabel}>
          <div className={appStyle.toggleControl}>
            <span>
              {botContent.ai_agent_bot_enabled ? "Enabled" : "Disabled"}
            </span>
            <input
              type="checkbox"
              checked={botContent.ai_agent_bot_enabled}
              onChange={toggleAIAgentBot}
              disabled={botContent.isTraining === 1}
              className={appStyle.toggleCheckbox}
            />
            <span className={appStyle.toggleSlider}></span>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              textAlign: "left",
            }}
          >
            <span className={appStyle.toggleText}>Enable AI Agent Bot</span>
            <p className={appStyle.helperText}>
              {botContent.ai_agent_bot_enabled
                ? "AI Bot features are enabled."
                : "AI Bot features are disabled."}
            </p>
          </div>
        </label>
      </div>

      {botContent.ai_agent_bot_enabled && (
        <div style={{ marginTop: "20px" }}>
          {/* AI Message Credits Dropdown */}
          <div className={appStyle.limitsDropdownSection}>
            <div
              className={appStyle.limitsDropdownHeader}
              onClick={() => setShowAiMessageCredits(!showAiMessageCredits)}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 15px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                marginBottom: showAiMessageCredits ? "0" : "15px",
                borderBottomLeftRadius: showAiMessageCredits ? "0" : "8px",
                borderBottomRightRadius: showAiMessageCredits ? "0" : "8px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                🔋 AI Message Credits & Limits
              </h3>
              <span
                style={{
                  fontSize: "20px",
                  color: "#007bff",
                  transform: showAiMessageCredits
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                ▼
              </span>
            </div>

            {showAiMessageCredits && (
              <div
                className={appStyle.limitsDropdownContent}
                style={{
                  border: "1px solid #e0e0e0",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  padding: "15px",
                  marginBottom: "15px",
                }}
              >
                <AIMessageCredits
                  limit={aiMessageLimit}
                  remaining={aiMessagesRemaining}
                  totalTopups={totalAiTopups}
                  isLoading={isLoadingAiLimit}
                />
              </div>
            )}
          </div>

          {/* Ingestion Storage Dropdown */}
          <div className={appStyle.limitsDropdownSection}>
            <div
              className={appStyle.limitsDropdownHeader}
              onClick={() => setShowIngestionStorage(!showIngestionStorage)}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 15px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                marginBottom: showIngestionStorage ? "0" : "15px",
                borderBottomLeftRadius: showIngestionStorage ? "0" : "8px",
                borderBottomRightRadius: showIngestionStorage ? "0" : "8px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                📦 Ingestion Storage & Limits
              </h3>
              <span
                style={{
                  fontSize: "20px",
                  color: "#007bff",
                  transform: showIngestionStorage
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                ▼
              </span>
            </div>

            {showIngestionStorage && (
              <div
                className={appStyle.limitsDropdownContent}
                style={{
                  border: "1px solid #e0e0e0",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  padding: "15px",
                  marginBottom: "15px",
                }}
              >
                <IngestionStorage
                  limit={ingestionLimit}
                  remaining={ingestionRemaining}
                  totalTopups={totalIngestionTopups}
                  isLoading={isLoadingIngestionLimit}
                />
              </div>
            )}
          </div>

          {/* Product Info Dropdown - Always render ProductInfo but wrap in dropdown */}
          <div className={appStyle.limitsDropdownSection}>
            <div
              className={appStyle.limitsDropdownHeader}
              onClick={() =>
                !productInfoLoading && setShowProductInfo(!showProductInfo)
              }
              style={{
                cursor: productInfoLoading ? "default" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 15px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                marginBottom: showProductInfo ? "0" : "15px",
                borderBottomLeftRadius: showProductInfo ? "0" : "8px",
                borderBottomRightRadius: showProductInfo ? "0" : "8px",
                opacity: productInfoLoading ? 0.7 : 1,
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                📦 Product Information
                {!productInfoHasData && !productInfoLoading && (
                  <span
                    style={{
                      marginLeft: "10px",
                      fontSize: "12px",
                      color: "#ff9800",
                      fontWeight: "normal",
                    }}
                  >
                    (Required for training)
                  </span>
                )}
              </h3>
              <span
                style={{
                  fontSize: "20px",
                  color: "#007bff",
                  transform: showProductInfo
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                {productInfoLoading ? "⏳" : "▼"}
              </span>
            </div>

            {/* Always render the ProductInfo component so it fetches on mount */}
            <div style={{ display: showProductInfo ? "block" : "none" }}>
              <div
                className={appStyle.limitsDropdownContent}
                style={{
                  border: "1px solid #e0e0e0",
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                  padding: "15px",
                  marginBottom: "15px",
                }}
              >
                <ProductInfo
                  ref={productInfoRef}
                  kbId={kbId}
                  tenantId={tenantId}
                  appName={appName}
                  region={region}
                  onDataFetched={handleProductInfoFetched}
                />
              </div>
            </div>
          </div>

          <h3>AI Agent Data Sources</h3>

          {/* Files Section */}
          <div className={appStyle.dataSourceSection}>
            <label className={appStyle.checkboxLabel}>
              <input
                type="checkbox"
                checked={filesEnabled}
                onChange={() => handleCheckboxChange("files")}
                disabled={isTraining}
                className={appStyle.checkbox}
              />
              <span className={appStyle.checkboxText}>📁 Files</span>
            </label>

            {filesEnabled && (
              <div className={appStyle.fileSection}>
                <div className={appStyle.uploadSection}>
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    onChange={handleFileUpload}
                    disabled={isTraining}
                    className={appStyle.fileInput}
                  />
                  <label
                    htmlFor="file-upload"
                    className={appStyle.uploadButton}
                  >
                    📎 Upload Files
                  </label>

                  {localFiles.length > 0 && (
                    <div className={appStyle.uploadHint}>
                      Selecting new files will add to existing ones
                    </div>
                  )}
                </div>

                {localFiles.length > 0 && (
                  <div className={appStyle.fileList}>
                    <h5>Uploaded Files ({localFiles.length})</h5>
                    {localFiles.map((file, index) => {
                      const isNewFile = file.isNew;

                      return (
                        <div
                          key={index}
                          className={`${appStyle.fileItem} ${fileUploadErrors[index] ? appStyle.fileItemError : ""}`}
                        >
                          <div className={appStyle.fileInfo}>
                            <span className={appStyle.fileName}>
                              {file.fileName}
                            </span>
                            <StatusIcon
                              status={file.status}
                              isTraining={isTraining}
                            />
                            {file.data_ids && file.data_ids.length > 0 && (
                              <span className={appStyle.dataIds}>
                                Data IDs: {file.data_ids.join(", ")}
                              </span>
                            )}
                          </div>

                          {!isNewFile && (
                            <div className={appStyle.fileOptions}>
                              <div className={appStyle.forceReingestCheckbox}>
                                <label className={appStyle.forceReingestLabel}>
                                  <input
                                    type="checkbox"
                                    checked={file.force_reingest}
                                    onChange={(e) =>
                                      handleFileForceReingestChange(
                                        index,
                                        e.target.checked,
                                      )
                                    }
                                    disabled={isTraining}
                                    className={appStyle.checkbox}
                                  />
                                  <span className={appStyle.forceReingestText}>
                                    Force Reingest
                                  </span>
                                  <InfoTooltip text="If new meaningful data has been added to this file, this helps reingest it." />
                                </label>
                              </div>

                              <div className={appStyle.reingestUploadSection}>
                                <input
                                  type="file"
                                  id={`reingest-file-${index}`}
                                  onChange={(e) =>
                                    handleReingestFileUpload(index, e)
                                  }
                                  disabled={isTraining || !file.force_reingest}
                                  className={appStyle.fileInput}
                                  style={{ display: "none" }}
                                />
                                <label
                                  htmlFor={`reingest-file-${index}`}
                                  className={`${appStyle.uploadButton} ${!file.force_reingest ? appStyle.uploadButtonDisabled : ""}`}
                                >
                                  📎 Upload New Version
                                </label>
                                {file.fileObject && (
                                  <div className={appStyle.uploadedFileInfo}>
                                    <span className={appStyle.uploadedFileName}>
                                      Selected: {file.fileObject.name}
                                    </span>
                                    <span className={appStyle.originalFileName}>
                                      Will replace: {file.fileName}
                                    </span>
                                  </div>
                                )}
                              </div>

                              <div className={appStyle.forceDeleteCheckbox}>
                                <label className={appStyle.forceDeleteLabel}>
                                  <input
                                    type="checkbox"
                                    checked={file.force_delete}
                                    onChange={(e) =>
                                      handleFileForceDeleteChange(
                                        index,
                                        e.target.checked,
                                      )
                                    }
                                    disabled={isTraining}
                                    className={appStyle.checkbox}
                                  />
                                  <span className={appStyle.forceDeleteText}>
                                    Force Delete
                                  </span>
                                  <InfoTooltip text="In case you want to delete already ingested content of this file before earlier, this helps you delete that knowledge source." />
                                </label>
                              </div>
                            </div>
                          )}

                          {fileUploadErrors[index] && (
                            <div className={appStyle.fileUploadError}>
                              {fileUploadErrors[index]}
                            </div>
                          )}

                          {isNewFile && (
                            <button
                              onClick={() => removeFile(index)}
                              disabled={isTraining}
                              className={appStyle.removeButton}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {getAllFailedFiles().length > 0 && (
                  <div className={appStyle.failedFilesContainer}>
                    <div
                      className={appStyle.failedFilesHeader}
                      onClick={() => setShowFailedFiles(!showFailedFiles)}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        marginTop: "15px",
                      }}
                    >
                      <span>
                        <strong>
                          Failed Files ({getAllFailedFiles().length})
                        </strong>
                      </span>
                      <span style={{ marginLeft: "8px", color: "#007bff" }}>
                        {showFailedFiles ? "Hide" : "Show"}
                      </span>
                    </div>

                    {showFailedFiles && (
                      <div className={appStyle.failedFilesList}>
                        {getAllFailedFiles().map((file, index) => (
                          <div key={index} className={appStyle.failedFileItem}>
                            <span className={appStyle.failedFileName}>
                              {file.fileName}
                            </span>
                            {file.message && (
                              <span className={appStyle.failedReason}>
                                {" "}
                                - {file.message}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* URLs Section */}
          <div className={appStyle.dataSourceSection}>
            <label className={appStyle.checkboxLabel}>
              <input
                type="checkbox"
                checked={urlsEnabled}
                onChange={() => handleCheckboxChange("urls")}
                disabled={isTraining}
                className={appStyle.checkbox}
              />
              <span className={appStyle.checkboxText}>🌐 URLs</span>
            </label>

            {urlsEnabled && (
              <div
                className={`${appStyle.urlSection} ${hasInvalidUrls ? appStyle.urlSectionError : ""}`}
              >
                <div className={appStyle.crawlingOptionsSection}>
                  <h5 className={appStyle.crawlingOptionsTitle}>
                    How to crawl pages:
                  </h5>
                  <div className={appStyle.crawlingOptionsGrid}>
                    <div className={appStyle.allowNestedSection}>
                      <label className={appStyle.allowNestedLabel}>
                        <input
                          type="checkbox"
                          checked={allowNested}
                          onChange={(e) => setAllowNested(e.target.checked)}
                          disabled={isTraining}
                          className={appStyle.checkbox}
                        />
                        <span className={appStyle.allowNestedText}>
                          Allow Nested URLs (follow links within pages)
                        </span>
                      </label>
                    </div>

                    <div className={appStyle.globalExploreClickablesSection}>
                      <label className={appStyle.globalExploreClickablesLabel}>
                        <input
                          type="checkbox"
                          checked={globalExploreClickables}
                          onChange={(e) => handleGlobalExploreClickablesChange(e.target.checked)}
                          disabled={isTraining}
                          className={appStyle.checkbox}
                        />
                        <span className={appStyle.globalExploreClickablesText}>
                          ⚡ Explore Clickables For All URLs
                        </span>
                        <InfoTooltip text="When enabled, Explore Clickables will be enabled for all URLs. You can still disable it for individual URLs below." />
                      </label>
                    </div>
                  </div>
                </div>

                <div className={appStyle.urlInputs}>
                  {localUrls.map((urlObj, index) => {
                    const isNewUrl = urlObj.isNew;

                    return (
                      <div key={index} className={appStyle.urlInputContainer}>
                        <div className={appStyle.urlInputRow}>
                          <div className={appStyle.urlInputWithStatus}>
                            <input
                              type="url"
                              value={urlObj.url}
                              onChange={(e) =>
                                handleUrlChange(index, e.target.value)
                              }
                              placeholder="Enter URL (e.g., https://example.com)"
                              disabled={isTraining || !isNewUrl}
                              className={`${appStyle.urlInput} ${urlErrors[index] ? appStyle.urlInputError : ""
                                }`}
                            />
                            <StatusIcon
                              status={urlObj.status}
                              isTraining={isTraining}
                            />
                          </div>

                          <div className={appStyle.perUrlExploreCheckbox}>
                            <label className={appStyle.perUrlExploreLabel}>
                              <input
                                type="checkbox"
                                checked={urlObj.explore_clickables || false}
                                onChange={(e) =>
                                  handleExploreClickablesChange(
                                    index,
                                    e.target.checked,
                                  )
                                }
                                disabled={isTraining}
                                className={appStyle.checkbox}
                              />
                              <span className={appStyle.perUrlExploreText}>
                                ⚡ Explore Clickables
                              </span>
                              <InfoTooltip text="It goes through all clickable elements on this specific URL and tries to find meaningful content hidden which might not be visible at first but hidden behind the clicks. Examples: Tabs, Accordions, Dropdowns, etc." />
                            </label>
                          </div>

                          {!isNewUrl && (
                            <div className={appStyle.forceReingestCheckbox}>
                              <label className={appStyle.forceReingestLabel}>
                                <input
                                  type="checkbox"
                                  checked={urlObj.force_reingest}
                                  onChange={(e) =>
                                    handleForceReingestChange(
                                      index,
                                      e.target.checked,
                                    )
                                  }
                                  disabled={isTraining}
                                  className={appStyle.checkbox}
                                />
                                <span className={appStyle.forceReingestText}>
                                  Force Reingest
                                </span>
                                <InfoTooltip text="If new meaningful data has been added on this page, this helps reingest it." />
                              </label>
                            </div>
                          )}

                          {!isNewUrl && (
                            <div className={appStyle.forceDeleteCheckbox}>
                              <label className={appStyle.forceDeleteLabel}>
                                <input
                                  type="checkbox"
                                  checked={urlObj.force_delete}
                                  onChange={(e) =>
                                    handleForceDeleteChange(
                                      index,
                                      e.target.checked,
                                    )
                                  }
                                  disabled={isTraining}
                                  className={appStyle.checkbox}
                                />
                                <span className={appStyle.forceDeleteText}>
                                  Force Delete
                                </span>
                                <InfoTooltip text="In case you want to delete already ingested content of this URL before earlier, this helps you delete that knowledge source." />
                              </label>
                            </div>
                          )}

                          {isNewUrl && localUrls.length > 1 && (
                            <button
                              onClick={() => removeUrlInput(index)}
                              disabled={isTraining}
                              className={appStyle.removeButton}
                            >
                              ✕
                            </button>
                          )}
                        </div>

                        {urlErrors[index] && (
                          <div className={appStyle.urlError}>
                            {urlErrors[index]}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {getAllFailedUrls().length > 0 && (
                    <div className={appStyle.failedUrlsContainer}>
                      <div
                        className={appStyle.failedUrlsHeader}
                        onClick={() => setShowFailedUrls(!showFailedUrls)}
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          <strong>
                            Failed URLs ({getAllFailedUrls().length})
                          </strong>
                        </span>
                        <span style={{ marginLeft: "8px", color: "#007bff" }}>
                          {showFailedUrls ? "Hide" : "Show"}
                        </span>
                      </div>

                      {showFailedUrls && (
                        <div className={appStyle.failedUrlsList}>
                          {getAllFailedUrls().map((u, index) => (
                            <div key={index} className={appStyle.failedUrlItem}>
                              <span className={appStyle.failedUrl}>
                                {u.url}
                              </span>
                              {u.message && (
                                <span className={appStyle.failedReason}>
                                  {" "}
                                  - {u.message}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={addUrlInput}
                  disabled={isTraining}
                  className={appStyle.addButton}
                >
                  + Add Another URL
                </button>

                {hasInvalidUrls && (
                  <div className={appStyle.urlSectionErrorText}>
                    Please fix the invalid URLs before saving
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sitemap Section */}
          <div className={appStyle.dataSourceSection}>
            <label
              className={`${appStyle.checkboxLabel} ${appStyle.disabledSection}`}
            >
              <input
                type="checkbox"
                checked={sitemapEnabled}
                onChange={() => handleCheckboxChange("sitemap")}
                disabled={true}
                className={appStyle.checkbox}
              />
              <span className={appStyle.checkboxText}>
                🗺️ Sitemap
                <span className={appStyle.comingSoonTag}>Coming Soon</span>
              </span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className={appStyle.actionButtons}>
            <button
              onClick={handleCancel}
              disabled={!hasChanges || isTraining}
              className={appStyle.cancelButton}
            >
              Cancel
            </button>

            <button
              onClick={() => {
                if (!validateUrls(localUrls)) {
                  return;
                } else {
                  handleRetrainAll();
                }
              }}
              disabled={
                !hasChanges ||
                !canRetrain ||
                trainingStatus === "loading" ||
                hasInvalidUrls ||
                isTraining
              }
              className={appStyle.retrainButton}
            >
              {getButtonText()}
            </button>
          </div>

          {/* Training Status Message */}
          {trainingMessage && (
            <div
              className={
                trainingStatus === "success"
                  ? appStyle.trainingSuccess
                  : trainingStatus === "partial_success"
                    ? appStyle.trainingPartialSuccess
                    : trainingStatus === "error"
                      ? appStyle.trainingError
                      : trainingStatus === "ready"
                        ? appStyle.trainingReady
                        : appStyle.trainingLoading
              }
            >
              {trainingMessage}
              {trainingResults && (
                <div className={appStyle.storageInfo}>
                  Storage Used: {trainingResults.storage_used_kb?.toFixed(2)}KB
                  / {trainingResults.storage_limit_kb}KB
                </div>
              )}
            </div>
          )}

          {/* Display current data IDs for reference */}
          {(localFiles.some((f) => f.data_ids?.length) ||
            urls.ingested.some((url) => url.data_ids?.length)) && (
              <div className={appStyle.dataIdsInfo}>
                <div
                  className={appStyle.dataIdsHeader}
                  onClick={() => setShowDataIds(!showDataIds)}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <h5 style={{ margin: 0 }}>Ingested Data IDs</h5>
                  <span style={{ color: "#007bff", fontSize: "14px" }}>
                    {showDataIds ? "Hide" : "Show"}
                  </span>
                </div>

                {showDataIds && (
                  <div className={appStyle.dataIdsContent}>
                    {localFiles.map(
                      (file, index) =>
                        file.data_ids?.length > 0 && (
                          <div
                            key={`file-${index}`}
                            className={appStyle.dataIdItem}
                          >
                            <strong>{file.fileName}:</strong>
                            <DataIdsButton
                              dataIds={file.data_ids}
                              kbId={botContent.kb_id}
                              region={region}
                            />
                          </div>
                        ),
                    )}
                    {urls.ingested.map(
                      (url, index) =>
                        url.data_ids?.length > 0 && (
                          <div
                            key={`url-${index}`}
                            className={appStyle.dataIdItem}
                          >
                            <strong>{url.url}:</strong>
                            <div style={{ marginTop: "5px" }}>
                              <DataIdsButton
                                dataIds={url.data_ids}
                                kbId={botContent.kb_id}
                                region={region}
                              />
                            </div>
                          </div>
                        ),
                    )}
                  </div>
                )}
              </div>
            )}
        </div>
      )}

      {/* Product Info Modal */}
      <Modal
        isOpen={showProductInfoModal}
        onClose={handleModalClose}
        hideCloseButton={false}
        title="📦 Product Information Required"
      >
        <div className={modalContentSty.modalContent}>
          <div className={modalContentSty.modalContentWrap}>
            <p className={modalContentSty.modalMessage}>
              Please provide information about your product to help the AI Agent
              understand your business better before training.
            </p>
            <ProductInfo
              ref={productInfoRef}
              kbId={kbId}
              tenantId={tenantId}
              appName={appName}
              region={region}
              isModal={true}
              hideTitle={true}
              onSaveSuccess={handleProductInfoSaveSuccess}
              
            />
          </div>
          <div className={modalContentSty.modalActions}>
            {productInfoHasData ? (
              <button
                className={`${modalContentSty.modalButton} ${modalContentSty.skipBtn}`}
                onClick={handleSkipProductInfo}
              >
                Keep Existing
              </button>
            ) : (
              <button
                className={`${modalContentSty.modalButton} ${modalContentSty.okBtn}`}
                onClick={handleProductInfoSaveSuccess}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}